import { useRef, useState } from 'react';

import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';

import { VERIFICATION_CHANNEL_NAMES } from '../utils/constants';
import {
  checkVerificationCodeMutationDefinition,
  sendVerificationCodeMutationDefinition
} from '../utils/queryDefinitions';

const CODE_REQUEST_DELAY = 300;

export default function useContactVerification() {
  const { pushToastNotification } = useToastNotifications();
  const [isFirstCodeSent, setIsFirstCodeSent] = useState(false);
  const timeoutRef = useRef(null);

  const { mutateAsync: _checkCode, isLoading: isCheckingCode } =
    useResourceMutation(checkVerificationCodeMutationDefinition());

  const { mutateAsync: _sendCode } = useResourceMutation(
    sendVerificationCodeMutationDefinition()
  );

  function sendCode(recipient) {
    clearTimeout(timeoutRef.current);

    // Our debounce util wasn't properly debouncing these calls.
    // Using this setTimeout instead.
    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await _sendCode({
          body: { verification: recipient }
        });

        const { success } = response.data || {};

        // Since the first code is sent automatically when the verification question loads,
        // only show the toast notification for subsequent (user-initiated) requests.
        if (success && isFirstCodeSent) {
          const channelName = VERIFICATION_CHANNEL_NAMES[recipient.channel];

          pushToastNotification({
            timeout: 3000,
            type: TOAST_NOTIFICATIONS_TYPES.SUCCESS,
            message: `Verification code sent to your ${channelName}.`
          });
        }

        if (success && !isFirstCodeSent) {
          setIsFirstCodeSent(true);
        }
      } catch (e) {
        const errorMessage =
          e.responseBody?.error_message ??
          'An error occurred while requesting the verification code.';

        pushToastNotification({
          timeout: 3000,
          type: TOAST_NOTIFICATIONS_TYPES.ERROR,
          message: errorMessage
        });
      }
    }, CODE_REQUEST_DELAY);
  }

  async function checkCode({ recipient, code }) {
    try {
      const validation = await _checkCode({
        body: { verification: { ...recipient, code } }
      });

      // If verification code is correct
      if (validation.data?.status !== 'approved') {
        // Incorrect verification code provided
        pushToastNotification({
          timeout: 3000,
          type: TOAST_NOTIFICATIONS_TYPES.ERROR,
          message: 'The verification code is incorrect.'
        });

        return { success: false };
      }

      const channelName = VERIFICATION_CHANNEL_NAMES[recipient.channel];

      pushToastNotification({
        timeout: 3000,
        type: TOAST_NOTIFICATIONS_TYPES.SUCCESS,
        message: `Your ${channelName} was verified.`
      });

      return { success: true };
    } catch (e) {
      // Render error message received from our API route if available
      const message =
        e.responseBody?.error_message ||
        'Unable to validate the provided verification code.';

      pushToastNotification({
        timeout: 3000,
        type: TOAST_NOTIFICATIONS_TYPES.ERROR,
        message
      });

      return {
        success: false,
        errorCode: e.responseBody?.error_code
      };
    }
  }

  return {
    sendCode,
    checkCode,
    isFirstCodeSent,
    isCheckingCode
  };
}
